/**
 * [HOTFIX Frontend Sprint 2026-07-10] EdgePlayerModal - shared modal
 * opened from Edge rows (Finds/Flags/Watchlist) + Diamond in the
 * Rough card. Reuses `PlayerAnalysisCanvas` for the actual layout so
 * the entire analytics surface stays a single source of truth.
 *
 * Close triggers: X button, Esc, click-outside. List state is preserved
 * because the parent lists never unmount - only this overlay does.
 */
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { X } from "lucide-react";
import { authHeaders } from "../../contexts/AuthContext";
import { PlayerAnalysisCanvas } from "../analytics/PlayerAnalysisCanvas";
import { UnlistedPill } from "../analytics/AdpDisplay";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EdgePlayerModal({
  edge,          // EdgeResult-shaped object (from /edge/* endpoints or Diamond)
  onClose,
  leagueId = null,
  seasonYear = 2025,
  scoringFormat = "half_ppr",
  isDark = true,
}) {
  const [canvas, setCanvas] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const overlayRef = useRef(null);

  // Esc + click-outside close.
  useEffect(() => {
    if (!edge) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [edge, onClose]);

  // Lazy fetch the PGF canvas for the clicked player.
  useEffect(() => {
    if (!edge?.player_id) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios
      .get(`${API}/analytics/players/${edge.player_id}/canvas`, {
        params: {
          season_year: seasonYear,
          scoring_format: scoringFormat,
          ...(leagueId ? { league_id: leagueId } : {}),
        },
        headers: authHeaders(),
      })
      .then((r) => {
        if (!cancelled) setCanvas(r.data || null);
      })
      .catch((e) => {
        if (!cancelled) setError(e?.response?.data?.detail || "Canvas unavailable");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [edge?.player_id, leagueId, seasonYear, scoringFormat]);

  if (!edge) return null;

  const player = {
    id: edge.player_id,
    name: edge.name,
    position: edge.position,
    nfl_team: edge.nfl_team,
    photo_url: edge.photo_url,
  };

  const handleOverlayClick = (e) => {
    if (e.target === overlayRef.current) onClose?.();
  };

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      data-testid="edge-player-modal-overlay"
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-start sm:items-center justify-center px-3 py-6 overflow-y-auto"
    >
      <div
        className={`relative w-full max-w-5xl rounded-2xl border ${
          isDark ? "border-white/10 bg-[#0F0F0F]" : "border-black/10 bg-white"
        } shadow-2xl p-4 sm:p-6`}
        data-testid="edge-player-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="edge-player-modal-title"
      >
        {/* Close button */}
        <button
          type="button"
          onClick={onClose}
          data-testid="edge-player-modal-close"
          aria-label="Close player analysis"
          className={`absolute top-3 right-3 rounded-full p-1.5 ${
            isDark ? "hover:bg-white/10 text-white/80" : "hover:bg-black/10 text-black/70"
          } transition-colors`}
        >
          <X className="w-4 h-4" />
        </button>

        {/* Header - name + reason from the edge row */}
        <div className="mb-3 pr-8">
          <h2
            id="edge-player-modal-title"
            data-testid="edge-player-modal-title"
            className={`font-display font-black text-2xl ${
              isDark ? "text-white" : "text-black"
            }`}
          >
            {edge.name}
          </h2>
          <p
            className={`font-mono text-[11px] mt-1 ${
              isDark ? "text-white/60" : "text-black/60"
            }`}
          >
            {edge.position}
            {edge.nfl_team ? ` · ${edge.nfl_team}` : ""}
            {edge.adp_rank_position != null ? (
              ` · ADP #${edge.adp_rank_position}`
            ) : (
              <>
                {" · ADP "}
                <UnlistedPill className="align-middle" />
              </>
            )}
            {edge.pgf_rank_position ? ` · PGF #${edge.pgf_rank_position}` : ""}
          </p>
          {edge.driving_axis && (
            <p
              className={`text-xs mt-2 uppercase tracking-widest font-mono ${
                isDark ? "text-white/70" : "text-black/70"
              }`}
              data-testid="edge-player-modal-driver"
            >
              Edge driver:{" "}
              <span className={`lowercase ${isDark ? "text-white" : "text-black"}`}>
                {edge.driving_axis}
              </span>
            </p>
          )}
        </div>

        {/* Canvas body */}
        <div
          className="max-h-[70vh] overflow-y-auto pr-1"
          data-testid="edge-player-modal-body"
        >
          {loading && (
            <div
              className={`text-center py-16 font-mono text-xs ${
                isDark ? "text-white/50" : "text-black/50"
              }`}
              data-testid="edge-player-modal-loading"
            >
              Loading analysis…
            </div>
          )}
          {!loading && error && (
            <div
              className="text-center py-16 font-mono text-xs text-[#FF6B00]"
              data-testid="edge-player-modal-error"
            >
              {error}
            </div>
          )}
          {!loading && !error && (
            <PlayerAnalysisCanvas
              player={player}
              pgfCanvas={canvas}
              analysisData={null}
              loading={false}
              error={null}
              isDark={isDark}
            />
          )}
        </div>
      </div>
    </div>
  );
}
